import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Eye, LogOut, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ImpersonationState {
  isImpersonating: boolean;
  entityType?: "ORGANIZATION" | "BUSINESS_UNIT" | "CUSTOMER";
  entityId?: string;
  entityName?: string;
  startedAt?: string;
}

export function ImpersonationBanner() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  // Only fetch impersonation state for SUPER admins (or when impersonating)
  const { data: impersonationState } = useQuery<ImpersonationState>({
    queryKey: ["impersonation-state"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/impersonation-state");
      if (!response.ok) {
        // If not authorized, just return not impersonating
        return { isImpersonating: false };
      }
      return response.json();
    },
    // Refetch periodically to stay in sync
    refetchInterval: 30000,
  });

  // Stop impersonation mutation
  const stopImpersonationMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/stop-impersonate");
      if (!response.ok) throw new Error("Failed to stop impersonation");
      return response.json();
    },
    onSuccess: async () => {
      const wasCustomerImpersonation = impersonationState?.entityType === "CUSTOMER";
      const entityId = impersonationState?.entityId;

      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ["impersonation-state"] });
      queryClient.invalidateQueries();
      toast({
        title: "Impersonation Ended",
        description: "Returned to admin view",
      });

      // Navigate back to admin dashboard
      if (wasCustomerImpersonation && entityId) {
        setLocation(`/customers?select=${entityId}`);
      } else {
        setLocation("/");
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Don't render if not impersonating
  if (!impersonationState?.isImpersonating) {
    return null;
  }

  const entityTypeLabel = impersonationState.entityType?.replace("_", " ") || "User";

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-2 py-1 bg-white/20 rounded-md">
              <Eye className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Impersonating</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">
                Viewing as: <strong>{impersonationState.entityName}</strong>
              </span>
              <span className="text-amber-100 text-sm">
                ({entityTypeLabel})
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-amber-100 hidden sm:inline">
              All actions are logged for security
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20 gap-2"
              onClick={() => stopImpersonationMutation.mutate()}
              disabled={stopImpersonationMutation.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Admin</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to check if impersonation is active (for layout adjustments)
export function useImpersonation() {
  const { data: impersonationState } = useQuery<ImpersonationState>({
    queryKey: ["impersonation-state"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/impersonation-state");
      if (!response.ok) {
        return { isImpersonating: false };
      }
      return response.json();
    },
    staleTime: 10000,
  });

  return {
    isImpersonating: impersonationState?.isImpersonating || false,
    entityType: impersonationState?.entityType,
    entityName: impersonationState?.entityName,
  };
}
